import { createSelector, createStructuredSelector } from 'reselect';

// Selectors
import { getCurrentBoardHasPasswordProtectedError } from '../element/board/boardPasswordProtectedSelector';
import {
    getIsCurrentVisibleBoardFetched,
    getIsCurrentVisibleBoardFetching,
    getIsCurrentVisibleBoardError,
    getIsCurrentBoardRoot,
    getIsCurrentVisibleBoardCached,
} from '../element/board/boardSelector';
import {
    getCurrentUser,
    isGuestSelector,
    isUserAvailableSelector,
    getCurrentUserRootBoardId,
} from '../user/currentUserSelector';
import { getDuplicateLoadingForCurrentBoard } from '../element/duplicate/elementDuplicateSelector';

import {
    getCurrentBoardIdFromState,
    getCurrentBoard,
    getCurrentVisibleBoard,
} from '../reducers/currentBoardId/currentBoardIdSelector';

import { getGridSize, getGridSizeName } from '../utils/grid/gridSizeSelector';

import {
    selectIsSocketConnected,
    selectSocketConnection,
    isSocketConnectionInitialised,
} from '../utils/socket/socketConnectionSelector';

import { breadcrumbSelector } from './breadcrumbs/breadcrumbSelector';

import {
    currentBoardAccessDeniedSelector,
    currentBoardUserPermissionsSelector,
    isCurrentBoardInTrashSelector,
    visibleBoardUserPermissionsSelector,
} from '../utils/permissions/elementPermissionsSelector';

import { getIsAnnotationModeEnabled } from '../element/drawing/annotation/store/annotationSelector';

import { getPlatformDetailsSelector } from '../platform/platformSelector';

// Utils
import { canWrite } from '../../common/permissions/permissionUtil';

const getIsDragging = (state) => state.getIn(['app', 'dragging', 'isDragging']);

const blockTemplateInteractionSelector = createSelector(
    visibleBoardUserPermissionsSelector,
    (visibleBoardPermissions) => visibleBoardPermissions === 0,
);

export const isEditableSelector = createSelector(currentBoardUserPermissionsSelector, canWrite);

const pageTitleSelector = createSelector(getCurrentBoard, getIsCurrentBoardRoot, (currentBoard, isRoot) =>
    currentBoard && !isRoot ? `${currentBoard.getIn(['content', 'title'])} - Milanote` : 'Milanote',
);

/**
 * If we determine, based on the hierarchy of a board, that it's in an error state, then
 * we shouldn't render the board (and we should force re-fetch it).
 *
 * Now it's the expectation that if a board is in an error state the server will respond with
 * an error, so this gives the server a chance to confirm the error, or resolve it by providing
 * updated data.
 */
const hasHierarchicalBoardErrorSelector = (state, ownProps) => {
    if (!getIsCurrentVisibleBoardFetched(state, ownProps)) return false;

    if (isCurrentBoardInTrashSelector(state)) return true;

    const isAccessDenied = currentBoardAccessDeniedSelector(state);

    if (!isAccessDenied) return false;

    if (getCurrentBoardHasPasswordProtectedError(state)) return false;

    return true;
};

const workspaceSelector = createStructuredSelector({
    currentBoard: getCurrentBoard,
    currentVisibleBoard: getCurrentVisibleBoard,
    currentBoardId: getCurrentBoardIdFromState,
    isCurrentBoardFetched: getIsCurrentVisibleBoardFetched,
    isCurrentBoardCached: getIsCurrentVisibleBoardCached,
    isCurrentBoardFetching: getIsCurrentVisibleBoardFetching,
    isCurrentBoardError: getIsCurrentVisibleBoardError,
    isHierarchicalBoardError: hasHierarchicalBoardErrorSelector,

    isCurrentBoardRoot: getIsCurrentBoardRoot,
    isEditable: isEditableSelector,
    pageTitle: pageTitleSelector,
    isCurrentBoardDuplicating: getDuplicateLoadingForCurrentBoard,
    permissions: currentBoardUserPermissionsSelector,
    rootBoardId: getCurrentUserRootBoardId,
    breadcrumbObj: breadcrumbSelector,

    drawingModeEnabled: getIsAnnotationModeEnabled,

    blockTemplateInteraction: blockTemplateInteractionSelector,

    gridSize: getGridSize,
    gridSizeName: getGridSizeName,
    isDragging: getIsDragging,

    socketConnected: selectIsSocketConnected,
    socketConnectionIsInitialised: isSocketConnectionInitialised,
    socketConnection: selectSocketConnection,

    isGuest: isGuestSelector,
    currentUser: getCurrentUser,
    isUserAvailable: isUserAvailableSelector,

    platformDetails: getPlatformDetailsSelector,
});

export default workspaceSelector;
